// Avviso di fine recupero (suono + vibrazione + notifica best-effort) con
// preferenze utente. Modulo singleton condiviso da RestTimer/WorkoutExecution/
// FreeWorkout.
//
// PERCHÉ il singleton audio: su iOS Safari un AudioContext creato FUORI da un
// gesto utente nasce "suspended" e resta muto. Il vecchio RestTimer creava il
// contesto al momento del finish (nessun gesto) → beep silenzioso su iPhone.
// Qui il contesto viene creato/sbloccato con unlockAudioAlert() chiamato nei
// gesture handler (tap che avvia il recupero, controlli del timer) e riusato
// al finish. iOS limita anche il numero di AudioContext → uno solo, riusato.

const PREFS_KEY = 'coachfit_alert_prefs';

let sharedCtx = null;
let autoResumeAttached = false;

// iOS sospende/interrompe l'AudioContext durante attese lunghe (dim dello
// schermo, altre app audio, background). Se al finish il contesto non è
// "running" e il resume avviene fuori da un gesto, il beep resta muto.
// Rimedio: OGNI tap dell'utente (e ogni ritorno in foreground) ri-sblocca il
// contesto, così durante l'allenamento resta quasi sempre attivo.
const resumeIfNeeded = () => {
  try {
    if (sharedCtx && sharedCtx.state !== 'running') sharedCtx.resume().catch(() => {});
  } catch (e) { /* ignore */ }
};

const attachAutoResume = () => {
  if (autoResumeAttached || typeof document === 'undefined') return;
  autoResumeAttached = true;
  ['touchend', 'click'].forEach((ev) =>
    document.addEventListener(ev, resumeIfNeeded, { capture: true, passive: true })
  );
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) resumeIfNeeded();
  });
};

/** Da chiamare DENTRO un gesture handler (tap): crea/sblocca il contesto audio. */
export const unlockAudioAlert = () => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!sharedCtx) sharedCtx = new AC();
    if (sharedCtx.state !== 'running') sharedCtx.resume().catch(() => {});
    attachAutoResume();
  } catch (e) { /* audio non disponibile */ }
};

/** Preferenze avvisi: { sound: bool, vibration: bool }. Default tutto attivo. */
export const getAlertPrefs = () => {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    const p = raw ? JSON.parse(raw) : {};
    return { sound: p.sound !== false, vibration: p.vibration !== false };
  } catch (e) {
    return { sound: true, vibration: true };
  }
};

export const setAlertPrefs = (partial) => {
  try {
    const next = { ...getAlertPrefs(), ...partial };
    localStorage.setItem(PREFS_KEY, JSON.stringify(next));
    return next;
  } catch (e) {
    return getAlertPrefs();
  }
};

/** La vibrazione è supportata dal browser? (iOS Safari: no) */
export const vibrationSupported = () =>
  typeof navigator !== 'undefined' && 'vibrate' in navigator;

const playBeepOn = (ctx) => {
  const playTone = (startTime, freq = 800) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = freq;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.4, startTime);
    gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.18);
    osc.start(startTime);
    osc.stop(startTime + 0.18);
  };
  // 3 beep brevi, l'ultimo più acuto (più riconoscibile)
  playTone(ctx.currentTime);
  playTone(ctx.currentTime + 0.22);
  playTone(ctx.currentTime + 0.44, 1000);
};

/**
 * Avviso di fine recupero. Rispetta le preferenze utente.
 * - suono: sul contesto sbloccato (resume difensivo prima di suonare);
 * - vibrazione: dove supportata (Android; iOS Safari non la espone);
 * - notifica best-effort SOLO se la pagina è nascosta e il permesso è già
 *   concesso (desktop/Android; su iOS il costruttore non esiste → no-op).
 */
export const playRestFinishedAlert = () => {
  const prefs = getAlertPrefs();

  if (prefs.sound) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC && !sharedCtx) sharedCtx = new AC(); // fallback (senza unlock può restare muto su iOS)
      if (sharedCtx) {
        if (sharedCtx.state !== 'running') {
          // resume + retry con guardia anti-doppio beep: copre anche lo stato
          // non-standard 'interrupted' di Safari
          let played = false;
          const playOnce = () => { if (!played) { played = true; playBeepOn(sharedCtx); } };
          sharedCtx.resume().then(playOnce).catch(() => {});
          setTimeout(() => {
            try { sharedCtx.resume().then(playOnce).catch(() => {}); } catch (e) { /* ignore */ }
          }, 400);
        } else {
          playBeepOn(sharedCtx);
        }
      }
    } catch (e) { /* ignore */ }
  }

  if (prefs.vibration && vibrationSupported()) {
    try { navigator.vibrate([200, 100, 200, 100, 300]); } catch (e) { /* ignore */ }
  }

  // Notifica best-effort quando la pagina non è visibile
  try {
    if (document.hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      new Notification('Recupero completato', {
        body: 'Il tempo di recupero è finito: torna al tuo allenamento!',
        // Stessa tag della push server (public/sw.js) → una notifica sostituisce
        // l'altra invece di accumularsi, anche in caso di sovrapposizione (PB-003).
        tag: 'coachfit-rest',
      });
    }
  } catch (e) { /* iOS: Notification constructor non supportato → ignora */ }
};
